import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  EquipmentTypedModel,
  EquipmentTypedValue,
  MaterialInputTypedValue,
} from 'procedures/ProcedureDomainTypes';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * The different subtypes of Parameter tables.
 * Agent keep in sync: benchling/notebook/structured_tables/constants/parameter_tables.py
 */
export enum ParameterStructuredTableMode {
  PARAMETER_CONFIRMATION = 'parameter_confirmation',
  PARAMETER_ACTUAL = 'parameter_actual', // This is displayed as Parameter Measurements in the UI
  MATERIAL_INPUT_CONFIRMATION = 'material_input_confirmation',
  MATERIAL_OUTPUT_CONFIRMATION = 'material_output_confirmation',
  EQUIPMENT_CONFIRMATION = 'equipment_confirmation',
}

// Agent keep in sync: benchling/notebook/structured_tables/constants/parameter_tables.py
export enum ParameterStructuredTableName {
  PARAMETER_CONFIRMATION = 'Confirm parameter setpoints',
  PARAMETER_ACTUAL = 'Record parameter measurements',
  MATERIAL_INPUT_CONFIRMATION = 'Confirm material inputs',
  MATERIAL_OUTPUT_CONFIRMATION = 'Confirm material outputs',
  EQUIPMENT_CONFIRMATION = 'Confirm equipment',
}

// Agent keep in sync: values from benchling/procedures/constants/parameter_fields.py
export enum ProcedureParameterSchemaFieldName {
  VALUE_FIELD_NAME = 'value',
  MATERIAL_FIELD_NAME = 'material',
  COMMENT_FIELD_NAME = 'comment',
  UNIT_FIELD_NAME = 'unit',
  AMOUNT_FIELD_NAME = 'amount',
  CONTAINER_FIELD_NAME = 'container',
  EQUIPMENT_FIELD_NAME = 'equipment',
}

// Agent keep in sync: benchling/notebook/structured_tables/constants/parameter_tables.py
export const ParameterStructuredTableModeToName: Record<ParameterStructuredTableMode, string> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: ParameterStructuredTableName.PARAMETER_CONFIRMATION,
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: ParameterStructuredTableName.PARAMETER_ACTUAL,
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]:
    ParameterStructuredTableName.MATERIAL_INPUT_CONFIRMATION,
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]:
    ParameterStructuredTableName.MATERIAL_OUTPUT_CONFIRMATION,
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: ParameterStructuredTableName.EQUIPMENT_CONFIRMATION,
};

export const ParameterStructuredTableModeToIcon: Record<ParameterStructuredTableMode, BenchlingIconName> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: 'parameters',
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'parameters',
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: 'material-input',
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: 'material-output',
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: 'microscope',
};

// describes the object type of each type of parameter table
export const ParameterStructuredTableModeToTypeName: Record<ParameterStructuredTableMode, string> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: 'parameter',
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'parameter',
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: 'material input',
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: 'material output',
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: 'equipment',
};

export const ParameterStructuredTableModeToTypeNamePlural: Record<ParameterStructuredTableMode, string> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: 'parameters',
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'parameters',
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: 'material inputs',
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: 'material outputs',
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: 'equipment',
};

export const PARAMETER_TABLE_MODES_THAT_ALLOW_PLATE_PARAMETERS: Array<ParameterStructuredTableMode> = [
  ParameterStructuredTableMode.PARAMETER_CONFIRMATION,
  ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION,
];

export interface ParameterInfo {
  name: string;
  versionId: string;
  paramId: string;
  plannedValueId: string | null;
  description: string;
  schemaFields: {[fieldName: string]: SchemaField};
  dataType: FieldType;
  confirmationMethod: ProcedureParameterConfirmationMethod;
  // Only relevant in execution
  conditionReplicateInfo?: {
    procedureMethodExecutionInstanceId: string;
    conditionId: string;
    conditionName: string;
    replicateId: string;
    replicateNumber: number;
    // Unit-operation (template) method version, shared across worksheets running it. Focus mode keys
    // persisted column widths by it. Absent when the template version is inaccessible.
    templateMethodDefinitionVersionId?: string;
  };
}
export interface DataParameterInfo extends ParameterInfo {
  value: string | null;
  unit: string | null;
  minimum: string | null;
  maximum: string | null;
}
export interface MaterialInputParameterInfo extends ParameterInfo {
  parent: string;
  plannedMaterial: MaterialInputTypedValue | string | null;
  plannedAmount: string | null;
}

export interface MaterialOutputParameterInfo extends ParameterInfo {}

export interface EquipmentParameterInfo extends ParameterInfo {
  plannedEquipment: EquipmentTypedValue | string | null;
  models: EquipmentTypedModel | string | null;
}
